import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Form, FormElement } from "@progress/kendo-react-form";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { useInventoryStore, useJobSetupStore } from "@presentation/stores";
import { useNotifications, JobSettingsFormBlock, useInjection, VersionSelectSettingsForm, NamingFormBlock, FastaFileFormBlock, RawFileFormBlock, JobPipelineSelectionBlock, LibraryFileFormBlock, ArchiveFileFormBlock } from "@presentation/components";
import { useLocation } from "react-router-dom";

import { CreateJob, JobPipelineTypeSchema } from "@domain/schemas/BI";
import { CreateJobService } from "@application/useCases";
import { extractProblemDetails, saveDuplicateJobMetadata } from "@presentation/common";
import { SNStepEnumSchema } from "@domain/schemas/BI";
import { formatStepName } from "./JobList";

const JobSetupForm: React.FC = () => {
  // acquire the items needed from the store
  const {
    CreateJobState,
    redirectToJobList,
    resetCreateJobState,
    allowCreationButton,
    setAllowCreationButton,
    rerunAvailableSteps,
    resumeFromStepId,
    sourceSoftwareVersion,
    setResumeFromStepId,
  } = useJobSetupStore((state) => ({
      CreateJobState: state.CreateJobState,
      redirectToJobList: state.redirectToJobList,
      handleRedirectToJobListChange: state.handleRedirectToJobListChange,
      resetCreateJobState: state.resetCreateJobState,
      allowCreationButton: state.allowCreationButton,
      setAllowCreationButton: state.setAllowCreationButton,
      rerunAvailableSteps: state.rerunAvailableSteps,
      resumeFromStepId: state.CreateJobState.resumeFromStepId,
      sourceSoftwareVersion: state.sourceSoftwareVersion,
      setResumeFromStepId: state.setResumeFromStepId,
    })
  );

  // import the resetSelectedGridData from the InventoryStore
  const { resetSelectedGridData } = useInventoryStore((state) => ({
      resetSelectedGridData: state.resetSelectedGridData,
    })
  );

  // Dialog state + submitting flag
  const [visible, setVisible] = React.useState<boolean>(false);
  const [submitting, setSubmitting] = React.useState<boolean>(false);

  const { t: loc } = useTranslation();
  const { pushNotification } = useNotifications();

  // pre-setup the CreateJobService and inject it
  const createJobService = useInjection(CreateJobService);
  const location = useLocation();
  const rerunJobId = React.useMemo(() => new URLSearchParams(location.search).get("rerunJobId"), [location.search]);
  const isRerunMode = !!rerunJobId;
  const stepTypeToStableId: Record<SNStepEnumSchema, string> = React.useMemo(() => ({
    [SNStepEnumSchema.DownloadData]: "11111111-1111-1111-1111-111111111111",
    [SNStepEnumSchema.PulsarStep1]: "22222222-2222-2222-2222-222222222222",
    [SNStepEnumSchema.PulsarStep2]: "33333333-3333-3333-3333-333333333333",
    [SNStepEnumSchema.PulsarStep3]: "44444444-4444-4444-4444-444444444444",
    [SNStepEnumSchema.LibraryGeneration]: "55555555-5555-5555-5555-555555555555",
    [SNStepEnumSchema.DiaAnalysis]: "66666666-6666-6666-6666-666666666666",
    [SNStepEnumSchema.SneCombine]: "88888888-8888-8888-8888-888888888888",
    [SNStepEnumSchema.SneMerge]: "77777777-7777-7777-7777-777777777777",
  }), []);
  const rerunStepOptions = React.useMemo(
    () => rerunAvailableSteps.map((step) => ({ id: stepTypeToStableId[step.type as SNStepEnumSchema], label: `${formatStepName(step.type)}` })),
    [rerunAvailableSteps, stepTypeToStableId],
  );
  const selectedRerunStep = rerunStepOptions.find((step) => step.id === resumeFromStepId) ?? null;
  const isRerunWithArchiveSelection = isRerunMode && (
    resumeFromStepId === stepTypeToStableId[SNStepEnumSchema.PulsarStep1]
    || resumeFromStepId === stepTypeToStableId[SNStepEnumSchema.LibraryGeneration]
  );

  const handleSubmit = () => {
    setVisible(true);
  };

  // function that will be run when user confirms the new job
  const handleDialogSubmit = async () => {
    if (submitting) return; // guard against double clicks
    setSubmitting(true);
    setVisible(false); // ⟵ close the dialog immediately (optimistic close)

    try {
      const createdJob = await createJobService.createJobAsync(CreateJobState as CreateJob);

      if (isRerunMode && rerunJobId && createdJob?.id) {
        saveDuplicateJobMetadata({
          jobId: createdJob.id,
          sourceJobId: rerunJobId,
          sourceJobName: CreateJobState.sourceJobName ?? "Unknown source job",
          sourceAnalysisName: CreateJobState.sourceAnalysisName ?? null,
          sourceCreatedAt: CreateJobState.sourceCreatedAt ?? null,
          sourceSoftwareVersion,
          duplicatedAt: new Date().toISOString(),
          restartFromStepId: CreateJobState.resumeFromStepId ?? null,
          reusedArtifacts: {
            rawFiles: (CreateJobState.rawFiles ?? []).map((file) => file.path),
            fastaFiles: CreateJobState.fastaFiles ?? [],
            libraryFiles: (CreateJobState.libraryFiles ?? []).map((file) => file.path),
            archiveFiles: (CreateJobState.archiveFiles ?? []).map((file) => file.path),
            softwareSettings: CreateJobState.softwareSettingsFile ? [CreateJobState.softwareSettingsFile] : [],
            conditionSetup: CreateJobState.conditionSetupFile ? [CreateJobState.conditionSetupFile] : [],
            modificationRepository: CreateJobState.modificationRepoFile ? [CreateJobState.modificationRepoFile] : [],
            reportSettings: CreateJobState.reportSettingsFiles ?? [],
            goFiles: CreateJobState.goFiles ?? [],
          },
        });
      }

      // Show success notification only if not redirecting (to avoid duplicate notifications)
      if (!redirectToJobList) {
        pushNotification(isRerunMode ? "Job duplicated and scheduled." : loc("newJobCreated"), "success");
      }

      if (redirectToJobList) {
        localStorage.setItem("jobCreationSuccess", "true");
      } else {
        localStorage.removeItem("jobCreationSuccess");
      }

      if (redirectToJobList) {
        window.location.href = "/";
      }
    } catch (error: unknown) {
      const { title, detail, messages, status } = extractProblemDetails(error);
      const header = title || loc("jobCreationFailed");
      const statusPart = status ? ` [${status}]` : "";
      const lines: string[] = [];
      if (detail?.trim()) lines.push(detail.trim());
      if (messages.length) lines.push(...messages.map((m) => m.trim()).filter(Boolean));

      const text = lines.length > 0 ? `${header}${statusPart}\n• ${lines.join("\n• ")}` : `${header}${statusPart}`;

      pushNotification(text, "error", 10000);
    } finally {
      setSubmitting(false);
    }
  };

  // success toast after redirect (if any)
  useEffect(() => {
    if (localStorage.getItem("jobCreationSuccess")) {
      pushNotification(isRerunMode ? "Job duplicated and scheduled." : loc("newJobCreated"), "success", 10000);
      localStorage.removeItem("jobCreationSuccess");
    }

    // enable/disable Create Job button
    const isLibraryBased = CreateJobState.pipelineType === JobPipelineTypeSchema.LibraryBased;
    const hasLibraryFiles = (CreateJobState.libraryFiles?.length ?? 0) > 0;
    const hasRawFiles = (CreateJobState.rawFiles?.length ?? 0) > 0;
    const hasArchiveFiles = (CreateJobState.archiveFiles?.length ?? 0) > 0;

    // Base validation
    let canCreate = (CreateJobState.name ?? "").trim() !== "" && (CreateJobState.softwareVersion ?? "").trim() !== "";

    // Additional validation for LibraryBasedAnalysis
    if (isLibraryBased) {
      canCreate = canCreate && hasLibraryFiles && hasRawFiles;
    } else if (CreateJobState.pipelineType === JobPipelineTypeSchema.LibraryGeneration) {
      canCreate = canCreate && CreateJobState.fastaFiles.length > 0 && (hasRawFiles || hasArchiveFiles);
    } else {
      // DirectDIA or others: Must have RawFiles
      canCreate = canCreate && CreateJobState.fastaFiles.length > 0 && hasRawFiles;
    }

    setAllowCreationButton(canCreate);
  }, [CreateJobState, setAllowCreationButton, loc, pushNotification, isRerunMode]);

  // define the reset function. We will use this to reset the form
  const resetForm = () => {
    resetCreateJobState();
    // also reset the selectedGridData using resetSelectedGridData function from the InventoryStore
    resetSelectedGridData();
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
      }}
    >
      {visible && (
          <Dialog
            className=""
            title={isRerunMode ? "A new job will be created from this job configuration and scheduled to run. Do you wish to proceed?" : "The current job setup will be scheduled to run once you confirm. Do you wish to proceed?"}
            onClose={() => setVisible(false)} // ⟵ always closable
          >
          {/* <Checkbox value={redirectToJobList} onChange={handleRedirectToJobListChange} label="Auto-redirect to job list" /> */}
          <DialogActionsBar>
            <Button themeColor="primary" onClick={handleDialogSubmit} disabled={submitting}>
              {submitting ? "Scheduling..." : "Confirm"}
            </Button>
            <Button onClick={() => setVisible(false)} disabled={submitting}>
              Cancel
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}

      <div style={{ flex: 1, overflowY: "auto", paddingLeft: "8px", paddingRight: "8px" }}>
        <Form
          onSubmit={handleSubmit}
          render={() => (
            <FormElement>

              <NamingFormBlock />

              <VersionSelectSettingsForm />
              {!isRerunMode && <JobPipelineSelectionBlock />}
              {isRerunMode && rerunStepOptions.length > 0 && (
                <fieldset className={"k-form-fieldset"}>
                  <legend className="k-form-legend">
                    <strong>Restart from:</strong>
                  </legend>
                  <div style={{ marginBottom: "10px" }}>
                    <DropDownList
                      style={{ width: "100%" }}
                      data={rerunStepOptions}
                      textField="label"
                      dataItemKey="id"
                      value={selectedRerunStep}
                      onChange={(event) => setResumeFromStepId(event.value?.id ?? null)}
                    />
                  </div>
                </fieldset>
              )}
              <RawFileFormBlock />
              {((CreateJobState.pipelineType === JobPipelineTypeSchema.DirectDIA || CreateJobState.pipelineType === JobPipelineTypeSchema.LibraryGeneration) && (!isRerunMode || isRerunWithArchiveSelection)) && (
                <ArchiveFileFormBlock />
              )}
              {CreateJobState.pipelineType === JobPipelineTypeSchema.LibraryBased && (
                <LibraryFileFormBlock />
              )}
              {CreateJobState.pipelineType !== JobPipelineTypeSchema.LibraryBased && (
                <FastaFileFormBlock />
              )}
              <JobSettingsFormBlock />



              <fieldset className={"k-form-fieldset"}>
                <div className="k-form-buttons">
                  <Button
                    type="submit"
                    themeColor={"primary"}
                    disabled={!allowCreationButton || submitting}
                    className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
                    onClick={handleSubmit}
                  >
                    {isRerunMode ? "Duplicate And Run" : "Create Job"}
                  </Button>
                  <Button type="reset" themeColor={"secondary"} className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base" onClick={resetForm} disabled={submitting}>
                    Reset
                  </Button>
                </div>
                <br />
              </fieldset>
            </FormElement>
          )}
        />
      </div>
    </div>
  );
};

export default JobSetupForm;
